import { useEffect, useRef } from 'react'
import { cn, isToday, minutesSinceMidnight, startOfDay } from '@/shared/lib'
import { groupByColumn } from '../lib/columns'
import { layoutColumn } from '../lib/layout'
import { hourMarks, type TimeWindow } from '../lib/window'
import type { Block, Column } from '../model/types'
import { BlockChip } from './block-chip'

export const HOUR_PX = 56
// Ниже этой ширины колонка перестаёт быть читаемой — сетка уходит в
// горизонтальный скролл вместо сжатия в полоски.
const MIN_COL_PX = 116
const HOUR_COL = '3.5rem'

interface ScheduleBoardProps {
  columns: Column[]
  blocks: Block[]
  window: TimeWindow
  now: Date
  onOpen: (block: Block) => void
}

/**
 * Часовая сетка с обобщённой колонкой: колонка — это день плюс необязательный
 * владелец (`Column`), поэтому один и тот же компонент даёт и «команда по
 * людям», и «моя неделя по дням».
 *
 * Одна скролл-область на обе оси: шапка липнет сверху, шкала часов — слева.
 * Раздельные контейнеры сломали бы sticky по горизонтали.
 */
export function ScheduleBoard({
  columns,
  blocks,
  window: timeWindow,
  now,
  onOpen,
}: ScheduleBoardProps) {
  const scrollRef = useRef<HTMLDivElement>(null)

  const spanMin = timeWindow.endMin - timeWindow.startMin
  const canvasHeight = (spanMin / 60) * HOUR_PX
  const groups = groupByColumn(blocks, columns)
  const marks = hourMarks(timeWindow)

  // Автоскролл в начало окна на каждую смену периода: сетка не должна открываться
  // посреди ночи только потому, что окно расширилось под ночной выход.
  const anchorKey = columns[0]?.key
  // biome-ignore lint/correctness/useExhaustiveDependencies: anchorKey — намеренный триггер перескролла при смене периода, в теле он не нужен
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 })
  }, [anchorKey])

  const headTemplate = {
    gridTemplateColumns: `${HOUR_COL} repeat(${columns.length}, minmax(0, 1fr))`,
  }
  const canvasStyle =
    columns.length > 1
      ? { minWidth: `calc(${HOUR_COL} + ${columns.length * MIN_COL_PX}px)` }
      : undefined

  return (
    <div
      ref={scrollRef}
      className="relative min-h-0 flex-1 overflow-auto overscroll-contain [scrollbar-gutter:stable]"
    >
      <div style={canvasStyle} className="min-w-full">
        <div className="sticky top-0 z-30 grid border-b bg-bg-base" style={headTemplate}>
          {/* Угол шкалы непрозрачен: под ним проезжают метки часов. */}
          <div className="sticky left-0 z-10 bg-bg-base" />
          {columns.map((column) => (
            <div
              key={column.key}
              className="flex min-w-0 flex-col items-center gap-0.5 border-l px-1 py-2"
            >
              <span
                className={cn(
                  'max-w-full truncate text-sm',
                  isToday(column.day) && column.ownerId === null
                    ? 'rounded-pill bg-accent px-2 text-text-inverse'
                    : 'text-text-primary',
                )}
              >
                {column.title}
              </span>
              {column.subtitle && (
                <span className="max-w-full truncate text-[11px] text-text-tertiary">
                  {column.subtitle}
                </span>
              )}
            </div>
          ))}
        </div>

        <div className="grid" style={headTemplate}>
          {/* Шкала часов липнет к левому краю: время остаётся на экране, пока
              сетка едет по горизонтали. */}
          <div
            className="sticky left-0 z-20 bg-bg-base"
            style={{ height: canvasHeight }}
            aria-hidden="true"
          >
            {marks.map((h) => {
              const top = ((h * 60 - timeWindow.startMin) / 60) * HOUR_PX
              return (
                <span
                  key={h}
                  // Метка на верхней кромке окна не центрируется по своей линии:
                  // половина уехала бы под шапку колонок, и первый час читался
                  // бы обрезанным.
                  className={cn(
                    'mono-num absolute right-2 text-[10px] text-text-tertiary',
                    top === 0 ? 'top-0' : '-translate-y-1/2',
                  )}
                  {...(top === 0 ? {} : { style: { top } })}
                >
                  {String(h % 24).padStart(2, '0')}:00
                </span>
              )
            })}
          </div>

          {columns.map((column) => {
            const positioned = layoutColumn(groups.get(column.key) ?? [], column.day)
            const today = isToday(column.day)
            const nowMin = minutesSinceMidnight(now)
            const nowVisible = today && nowMin >= timeWindow.startMin && nowMin <= timeWindow.endMin
            const dayStart = startOfDay(column.day)

            return (
              <div key={column.key} className="relative border-l" style={{ height: canvasHeight }}>
                {marks.map((h) => (
                  <div
                    key={h}
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 border-t border-border-subtle"
                    style={{ top: ((h * 60 - timeWindow.startMin) / 60) * HOUR_PX }}
                  />
                ))}

                {nowVisible && (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 z-10 flex items-center"
                    style={{ top: ((nowMin - timeWindow.startMin) / 60) * HOUR_PX }}
                  >
                    <span className="-ml-0.5 size-1.5 rounded-pill bg-now" />
                    <span className="h-px flex-1 bg-now" />
                  </div>
                )}

                {positioned.map(({ block, startMin, endMin, col, cols, conflict }) => {
                  const top = ((startMin - timeWindow.startMin) / 60) * HOUR_PX
                  const height = ((endMin - startMin) / 60) * HOUR_PX
                  return (
                    <button
                      key={block.id}
                      type="button"
                      onClick={() => onOpen(block)}
                      className="absolute px-px focus:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
                      style={{
                        top,
                        height,
                        left: `${(col / cols) * 100}%`,
                        width: `${(1 / cols) * 100}%`,
                      }}
                    >
                      <BlockChip
                        block={block}
                        conflict={conflict}
                        past={block.slot.endsAt < now}
                        compact={height < 40}
                      />
                    </button>
                  )
                })}

                {/* Ночной хвост предыдущих суток начинается выше нуля — метка
                    для скринридера, чтобы «01:00» не выглядело оторванным. */}
                <span className="sr-only">{`Колонка ${column.title}, ${dayStart.toDateString()}`}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
